import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ICONS, formatPrice, rub, tr, uploadUrl } from "../lib/format";
import { Send, ArrowUpRight } from "lucide-react";

function useList(path, key) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(path).then(({ data }) => setItems(data[key])).catch(() => setItems([]));
  }, [path, key]);
  return items;
}

const SERVICES = [
  { id: "ozon-wb", key: "ozon", ru: "Товарка Ozon / WB", en: "Ozon / WB", cn: "电商图卡", price: "от 3 000 ₽", tag: "E-COMMERCE", features: ["3× ревизии", "Фотореалистичные сцены", "A/B тест визуала"] },
  { id: "3d-logos", key: "logo", ru: "3D-Логотипы", en: "3D Logos", cn: "3D 徽标", price: "от 20 000 ₽", tag: "BRANDING", features: ["3 концепции 01/02/03", "8K + анимация", "Исходники OBJ/FBX"] },
  { id: "game-art", key: "game", ru: "Гейм-арт & Концепты", en: "Game Art", cn: "游戏原画", price: "от 5 000 ₽", tag: "GAME ART", features: ["Кинематографичный свет", "Cyberpunk мудборд", "Композинг сцен"] },
  { id: "ai-websites", key: "site", ru: "Сайты & Лендинги", en: "Websites", cn: "网站", price: "от 25 000 ₽", tag: "WEB", features: ["Glassmorphism UI", "Telegram-заявки", "100% адаптив"] },
  { id: "retainer", key: "ret", ru: "Ретейнер / Арт-дирекшн", en: "Retainer", cn: "月费服务", price: "от 70 000 ₽ / мес", tag: "SUBSCRIPTION", features: ["24/7 приоритет", "До 30 креативов/мес", "Стратегия визуала"] },
];

const PORTFOLIO = [
  { id: "case-ozon", title: "Ozon CTR 4.6%", metric: "+4.6% CTR", cat: "E-COMMERCE", img: "https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjAzZCUyMG5lb24lMjBhcnQlMjBBSSUyMGNyZWF0b3IlMjBsdXh1cnklMjBnb2xkJTIwY3lhbnxlbnwwfHx8fDE3ODg4NjA2OTJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: "case-logos", title: "3D Logos 01/02/03", metric: "Gold × Cyan", cat: "BRANDING", img: "https://images.unsplash.com/photo-1769461766792-3993f0ab1b02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHwzZCUyMGxvZ28lMjByZW5kZXIlMjBsdXh1cnklMjBnb2xkJTIwY3lhbnxlbnwwfHx8fDE3ODg4NjA3MDJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: "case-drift", title: "Black Russia Drift", metric: "High-Octane", cat: "GAME ART", img: "https://images.unsplash.com/photo-1555532686-d0fccaccadcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjYXIlMjBkcmlmdCUyMG5lb24lMjBuaWdodHxlbnwwfHx8fDE3ODYwODY3ODR8MA&ixlib=rb-4.1.0&q=85" },
  { id: "case-foshan", title: "Foshan SK Signage", metric: "Guangdong · CN", cat: "PHYSICAL", img: "https://images.unsplash.com/photo-1613535167523-0da946b83ffb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxjeWJlcnB1bmslMjAzZCUyMG5lb24lMjBhcnQlMjBBSSUyMGNyZWF0b3IlMjBsdXh1cnklMjBnb2xkJTIwY3lhbnxlbnwwfHx8fDE3ODg4NjA2OTJ8MA&ixlib=rb-4.1.0&q=85" },
];

const SKILLS = ["Midjourney v6", "Stable Diffusion", "ComfyUI", "Blender 3D", "Runway Gen-2", "Ozon/WB", "Supply Chain CN"];

function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative pt-32 sm:pt-40 pb-24 overflow-hidden" data-testid="hero-section">
      <div className="hero-halo" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <span className="chip fade-up">{t.hero.badge}</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[1.02] tracking-tight mt-6 fade-up delay-100">
              <span className="block">{t.hero.title.split("&")[0]}<span className="text-gold gold-glow">&</span></span>
              <span className="block text-cyan cyan-glow">{t.hero.title.split("&")[1]}</span>
            </h1>
            <p className="mt-6 text-gray-300 text-lg sm:text-xl max-w-2xl leading-relaxed fade-up delay-200">{t.hero.sub}</p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up delay-300">
              <a href="#contact" className="btn-gold inline-flex items-center gap-2" data-testid="hero-cta-contact">
                {t.hero.cta1} <Send size={16} />
              </a>
              <a href="#portfolio" className="btn-outline inline-flex items-center gap-2" data-testid="hero-cta-portfolio">
                {t.hero.cta2} <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 fade-up delay-400">
            <div className="glass rounded-3xl p-6 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full" style={{background: "radial-gradient(circle, rgba(212,175,55,0.4), transparent 70%)"}}/>
              <div className="grid grid-cols-2 gap-6 relative">
                {t.hero.stats.map((s, i) => (
                  <div key={i} className="fade-up" style={{animationDelay: `${0.3 + i * 0.08}s`}}>
                    <div className="font-heading text-3xl sm:text-4xl font-extrabold text-gold gold-glow">{s.v}</div>
                    <div className="text-xs mt-1 tracking-wider text-gray-400 uppercase">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const { t } = useI18n();
  return (
    <section id="about" className="py-24 sm:py-32" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="chip">/ 01</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">{t.about.title}</h2>
            <div className="divider-gold my-6"/>
          </div>
          <div className="lg:col-span-7">
            <p className="text-gray-100 text-2xl sm:text-3xl font-heading font-light leading-snug">{t.about.p1}</p>
            <p className="text-gray-300 mt-6 leading-relaxed text-lg">{t.about.p2}</p>
            <div className="mt-10">
              <div className="text-xs tracking-widest uppercase text-gold mb-4">{t.about.skills}</div>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((s) => <span key={s} className="chip">{s}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { t, lang } = useI18n();
  const services = useList("/services", "services");
  return (
    <section id="services" className="py-24 sm:py-32 relative" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="chip">/ 02</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">{t.services.title}</h2>
            <p className="text-gray-400 mt-3">{t.services.sub}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, idx) => {
            const Icon = ICONS[s.icon] || ICONS.Sparkles;
            const features = s[`features_${lang}`]?.length ? s[`features_${lang}`] : s.features_ru;
            return (
            <div key={s.id} className={`glass rounded-2xl p-8 card-hover fade-up delay-${(idx % 4) * 100 + 100}`} data-testid={`service-card-${s.id}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="chip">{s.category}</span>
                <Icon size={18} className="text-cyan" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-2">{tr(s, "title", lang)}</h3>
              <p className="text-sm text-gray-400">{tr(s, "description", lang)}</p>
              <div className="text-gold text-3xl font-heading font-bold my-4" data-testid={`service-price-${s.id}`}>{formatPrice(s, lang)}</div>
              {s.tiers.length > 0 && (
                <div className="space-y-1 mb-3">
                  {s.tiers.map((tier, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-white/5 py-1">
                      <span className="text-gray-300">{tier[`name_${lang}`] || tier.name_ru}</span>
                      <span className="text-gold font-mono">{rub(tier.price)}</span>
                    </div>
                  ))}
                </div>
              )}
              <ul className="space-y-2 mt-4">
                {features.map((f) => (
                  <li key={f} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-cyan mt-1">◆</span>{f}
                  </li>
                ))}
              </ul>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Portfolio() {
  const { t, lang } = useI18n();
  const items = useList("/portfolio", "portfolio");
  return (
    <section id="portfolio" className="py-24 sm:py-32" data-testid="portfolio-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="chip">/ 03</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">{t.portfolio.title}</h2>
            <p className="text-gray-400 mt-3">{t.portfolio.sub}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {items.map((p, i) => (
            <a key={p.id} href="#contact" className={`group relative overflow-hidden rounded-2xl glass card-hover fade-up delay-${(i % 4) * 100 + 100}`}
               data-testid={`portfolio-card-${p.id}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={uploadUrl(p.image_url)} alt={tr(p, "title", lang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                <div className="absolute inset-0" style={{background: "linear-gradient(180deg, transparent 40%, rgba(6,6,10,0.95))"}}/>
                {p.category && <div className="absolute top-4 left-4"><span className="chip">{p.category}</span></div>}
                <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full glass-cyan flex items-center justify-center">
                  <ArrowUpRight size={16} className="text-cyan"/>
                </div>
              </div>
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-xl font-semibold">{tr(p, "title", lang)}</h3>
                </div>
                <span className="text-gold font-mono text-xs tracking-wider">{p.metric}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", project_type: t.contact.types[0], budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, language: lang.toUpperCase() });
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", project_type: t.contact.types[0], budget: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || t.contact.err);
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 relative" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="chip">/ 04</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">{t.contact.title}</h2>
            <div className="divider-gold my-6"/>
            <p className="text-gray-300 leading-relaxed">{t.contact.sub}</p>
            <div className="mt-8 space-y-4">
              <a href="https://t.me/Blud_Error" target="_blank" rel="noopener noreferrer" className="link-cyan" data-testid="contact-telegram-link">Telegram: @Blud_Error</a><br/>
              <a href="https://www.instagram.com/artem_ai_pro" target="_blank" rel="noopener noreferrer" className="link-cyan" data-testid="contact-instagram-link">Instagram: @artem_ai_pro</a><br/>
              <span className="text-gray-400 text-sm">Guangzhou · China · Worldwide</span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label>{t.contact.name}</label>
                  <input required value={form.name} onChange={set("name")} className="mt-2" data-testid="contact-name-input"/>
                </div>
                <div>
                  <label>{t.contact.email}</label>
                  <input required type="email" value={form.email} onChange={set("email")} className="mt-2" data-testid="contact-email-input"/>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label>{t.contact.project}</label>
                  <select value={form.project_type} onChange={set("project_type")} className="mt-2" data-testid="contact-project-select">
                    {t.contact.types.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </div>
                <div>
                  <label>{t.contact.budget}</label>
                  <input required value={form.budget} onChange={set("budget")} placeholder="₽ / $ / ¥" className="mt-2" data-testid="contact-budget-input"/>
                </div>
              </div>
              <div>
                <label>{t.contact.message}</label>
                <textarea required rows={5} value={form.message} onChange={set("message")} className="mt-2" data-testid="contact-message-textarea"/>
              </div>
              <button type="submit" disabled={loading} className="btn-gold inline-flex items-center gap-2" data-testid="contact-submit-button">
                {loading ? "…" : t.contact.submit} <Send size={16}/>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 flex flex-wrap items-center justify-between gap-4">
        <span className="text-sm text-gray-400 font-mono">© 2026 ARVEX · voxer.online</span>
        <span className="text-xs text-gray-500 font-mono tracking-wider uppercase">Deep Black · Gold · Neon Cyan</span>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="grid-noise min-h-screen">
      <Hero/>
      <About/>
      <Services/>
      <Portfolio/>
      <Contact/>
      <Footer/>
    </div>
  );
}
